<script setup>
  import { computed } from 'vue';
  import { useUserStore } from "@/stores/UserStore";
  import { useStormStore } from "@/stores/StormStore";
  import { useUnitConverter } from '@/composables/useUnitConverter';
  import Toggle from "@/components/controls/Toggle.vue";  
  import Icon from "@/components/icons/Icon.vue";

  const props = defineProps({
    storm: {
      type: Object,
      required: true
    },
    allEnabled: {
      type: Boolean,
      default: false
    },
    showActive: {
      type: Boolean,
      default: true
    },
    showToggle: {
      type: Boolean,
      default: true
    },
    showPlotTypes: {
      type: Boolean,
      default: false
    }
  });

  const userStore = useUserStore();
  const stormStore = useStormStore();

  const unitConverter = useUnitConverter();

  const isSelected = computed(() => props.storm.id === stormStore.selectedStorm?.id ? 'active' : 'inactive');
  const isHurricane = computed(() => props.storm.status.type.toLowerCase() === 'hu');
  const isInvest = computed(() => props.storm.status.stormfullname.toLowerCase().includes('invest'));
  const category = computed(() => isHurricane.value ? stormStore.stormCategory(props.storm.id) : props.storm.status.type.toUpperCase());
  const showViewInvestMessage = computed(() => stormStore.isStormSelected(props.storm.id) && stormStore.plotType === 'cone' && isInvest.value && !props.allEnabled);
  
  const isDisabled = computed(() => (
    props.allEnabled 
    ? false 
    : !stormStore.visibleStorms.has(props.storm.id) 
  ));

  const windSpeed = computed(() => {
    return (storm) => {
      const windSpeedKts = storm.status.windsustained_kts;
      const usersWindSpeed = getKtsSpeedInUsersUnits(windSpeedKts);

      return `${usersWindSpeed} <span class="jwx-storm-card__inline-meta-unit">${userStore.units.wind}</span>`;
    }    
  });

  const speed = computed(() => {
    return (storm) => {
      const speedKts = storm.status.speed_kts;
      const usersSpeed = getKtsSpeedInUsersUnits(speedKts);

      return `${usersSpeed} <span class="jwx-storm-card__inline-meta-unit">${userStore.units.wind}</span>`;
    }   
  })

  function getKtsSpeedInUsersUnits(speed) {
    return unitConverter.convert('kts', userStore.units.wind, speed, true)
  }
</script>

<template>
  <button
    type="button"
    class="jwx-storm-card__button block-card" 
    style="--padding: .625rem;"
    data-has-description
    :data-is-hurricane="isHurricane"
    :data-is-invest="isInvest"
    :data-state="props.showActive ? isSelected : null"
    :data-storm-category="category"
    :id="props.storm.id"
    :disabled="isDisabled"
  >
    <div class="jwx-storm-card__header">
      <h3 class="jwx-storm-card__name">{{ props.storm.status.stormfullname }}</h3>
      <Toggle
        v-if="props.showToggle"
        @click.stop
        @input:change="$emit('storm-change', $event)"
        :id="`storm-${props.storm.status.stormfullname}`"
        :value="props.storm.id"
        v-model="stormStore.visibleStorms"
        class="jwx-storm-card__toggle"
        style="--jwx-toggle-switch-track-active: white;"
        size="small"
      >
      </Toggle>
    </div>
      
    <ul v-if="isInvest" class="jwx-storm-card__inline-meta-list">
      <li class="jwx-source-card__inline-meta-item" aria-label="Latitude / Longitude">
        <Icon name="location-marker" class="jwx-source-card__inline-meta-icon" />
        {{ props.storm.status.lat }} / {{ props.storm.status.lon }}
      </li>
      <li v-if="showPlotTypes" class="jwx-source-card__inline-meta-item jwx-m-left-auto" aria-label="Latitude / Longitude">
        <Icon name="spaghetti-plot" class="jwx-source-card__inline-meta-icon" />
      </li>
    </ul>
      
    <ul v-else class="jwx-storm-card__inline-meta-list">
      <li class="jwx-source-card__inline-meta-item" aria-label="Wind Speed">
        <Icon name="markers" class="jwx-source-card__inline-meta-icon" style="--size: .75rem" />
        <p v-html="windSpeed(props.storm)"></p>
      </li>
      <li class="jwx-source-card__inline-meta-item" aria-label="Direction">
        <Icon name="compass" class="jwx-source-card__inline-meta-icon" />
        {{ props.storm.status.directioncompass }}
      </li>
      <li class="jwx-source-card__inline-meta-item" aria-label="Speed">
        <Icon name="speed-gauge" class="jwx-source-card__inline-meta-icon" />
        <p v-html="speed(props.storm)"></p>
      </li>
      <li v-if="showPlotTypes" class="jwx-source-card__inline-meta-item jwx-m-left-auto" aria-label="Latitude / Longitude">
        <Icon v-if="props.storm.spaghetti" name="spaghetti-plot" class="jwx-source-card__inline-meta-icon" />
        <Icon name="cone-plot" class="jwx-source-card__inline-meta-icon" />
      </li>      
    </ul>

    <p v-if="showViewInvestMessage" class="jwx-storm-card__message">Switch to <b>Spaghetti Plot mode</b> to view invest.</p>

    <div class="jwx-storm-card__storm-badge" >
      <span class="jwx-storm-card__storm-category">{{ category }}</span>
    </div>
  </button>
</template>